<template>
  <!-- 聊天窗口 -->
  <div
    v-if="visible"
    class="chat-window"
    :style="windowStyle"
  >
    <!-- 顶部标题栏(可拖拽) -->
    <header class="chat-header" @mousedown="startDrag">
      <div class="header-left">
        <div class="header-icon">
          <i class="el-icon-chat-line-round"></i>
        </div>
        <div class="header-title">
          <div class="title-main">
            {{ selectedGroup.name || '即时通讯' }}
          </div>
          <div class="title-sub">
            <span class="status-dot"></span>
            <span>{{ statusText }}</span>
          </div>
        </div>
      </div>
      <div class="header-actions">
        <el-tooltip content="关闭" placement="bottom">
          <button class="action-btn close" @click.stop="handleClose">
            <i class="el-icon-close"></i>
          </button>
        </el-tooltip>
      </div>
    </header>

    <!-- 主体区域 -->
    <div class="chat-body">
      <!-- 左侧群组列表 -->
      <aside class="chat-sidebar">
        <div class="sidebar-header">
          <div class="sidebar-title">群聊</div>
        </div>
        <div class="group-list" v-if="filteredGroupList.length">
          <div
            v-for="item in filteredGroupList"
            :key="item.id"
            class="group-item"
            :class="{ active: selectedGroup.id === item.id, unread: item.unread }"
            @click="selectGroup(item)"
          >
            <div class="group-avatar">
              <span class="avatar-text">群</span>
              <span v-if="item.unread" class="unread-badge">
                {{ item.unread > 99 ? '99+' : item.unread }}
              </span>
            </div>
            <div class="group-info">
              <div class="group-row">
                <span class="group-name">{{ item.name }}</span>
                <span class="group-time">{{ item.lastTime || '' }}</span>
              </div>
              <div class="group-preview">
                <span v-if="item.lastMessage" class="preview-text">
                  {{ item.lastMessage }}
                </span>
                <!-- <span v-if="item.memberCount" class="member-tag">
                  {{ item.memberCount }}人
                </span> -->
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <i class="el-icon-folder-opened"></i>
          <p>{{ searchKeyword ? '未找到匹配群聊' : '暂无群聊' }}</p>
        </div>
      </aside>

      <!-- 右侧聊天内容 -->
      <main class="chat-main">
        <!-- 聊天头部(可拖拽) -->
        <div class="chat-top" @mousedown="startDrag">
          <div
            class="top-avatar"
            :class="{ clickable: selectedGroup.id }"
            @click.stop="openMemberDialog"
          >
            <span class="avatar-text" v-if="selectedGroup.id">群</span>
            <i v-else class="el-icon-chat-dot-round"></i>
          </div>
          <div class="top-info">
            <div class="top-name">
              {{ selectedGroup.name || '请选择群聊' }}
            </div>
            <!-- <div class="top-meta" v-if="selectedGroup.memberList && selectedGroup.memberList.length">
              {{ selectedGroup.memberList.length }} 位成员
            </div> -->
            <!-- <div class="top-meta" v-else-if="selectedGroup.id">
              点击头像查看成员
            </div> -->
          </div>
          <!-- <div class="top-extra" v-if="selectedGroup.id">
            <el-tooltip content="群成员" placement="bottom">
              <i class="el-icon-user-solid" @click.stop="openMemberDialog"></i>
            </el-tooltip>
          </div> -->
        </div>

        <!-- 消息列表 -->
        <div class="chat-content" ref="chatList">
          <div v-if="!chatMessages.length" class="empty-messages">
            <div class="empty-icon">
              <i class="el-icon-position"></i>
            </div>
            <p>还没有消息,来打个招呼吧 👋</p>
          </div>
          <template v-else>
            <div
              v-for="item in renderedMessages"
              :key="item.key"
            >
              <!-- 时间分隔 -->
              <div v-if="item.kind === 'time'" class="time-divider">
                <span>{{ item.time }}</span>
              </div>
              <!-- 消息 -->
              <div
                v-else
                class="message-wrapper"
                :class="item.message.type === 'own' ? 'own' : 'other'"
              >
                <div
                  class="avatar"
                  :class="getAvatarClass(item.message)"
                  :title="item.message.senderName || ''"
                >
                  {{ getAvatarText(item.message) }}
                </div>
                <div class="message-body">
                  <div
                    v-if="item.message.type === 'other' && item.message.senderName"
                    class="sender-name"
                  >
                    {{ item.message.senderName }}
                  </div>
                  <div
                    class="bubble"
                    :class="[
                      item.message.type === 'own' ? 'bubble-own' : 'bubble-other',
                      { 'is-new': item.message._isNew }
                    ]"
                  >
                    {{ item.message.content }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- 底部输入区 -->
        <div class="chat-footer">
          <div class="input-wrapper">
            <textarea
              v-model="inputMessage"
              placeholder="请输入消息内容..."
              class="message-input"
              rows="1"
              @keydown.enter.exact.prevent="sendMessage"
              @input="onInput"
            />
          </div>
          <div class="footer-actions">
            <span class="char-count" :class="{ overflow: inputMessage.length > 500 }">
              {{ inputMessage.length }} / 500
            </span>
            <button
              class="send-button"
              :class="{ disabled: !canSend }"
              :disabled="!canSend"
              @click="sendMessage"
            >
              <span>发送</span>
              <i class="el-icon-position"></i>
            </button>
          </div>
        </div>
      </main>
    </div>

    <!-- 群成员抽屉(从聊天窗口右侧滑出,微信/钉钉电脑版风格) -->
    <el-drawer
      :visible.sync="memberDialogVisible"
      direction="rtl"
      size="65%"
      :with-header="false"
      :modal="false"
      :wrapper-closable="true"
      custom-class="member-drawer"
      append-to-body
    >
      <div class="member-drawer-inner">
        <!-- 抽屉顶部 -->
        <div class="member-drawer-header">
          <div class="header-content">
            <div class="header-avatar">
              <span class="avatar-text">群</span>
            </div>
            <div class="header-info">
              <div class="header-name">{{ selectedGroup.name || '群聊' }}</div>
              <div class="header-meta">
                <span>{{ (selectedGroup.memberList || []).length }} 位成员</span>
              </div>
            </div>
          </div>
          <button class="header-close" @click="memberDialogVisible = false" title="关闭">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <!-- 抽屉主体 -->
        <div class="member-drawer-body" v-if="selectedGroup.memberList && selectedGroup.memberList.length">
          <!-- 成员列表 -->
          <div class="member-section">
            <div class="section-title">
              <span>群成员</span>
              <span class="section-count">{{ selectedGroup.memberList.length }}</span>
            </div>
            <div class="member-grid">
              <div
                v-for="(member, index) in selectedGroup.memberList"
                :key="index"
                class="member-card"
                :class="getMemberRoleClass(index)"
              >
                <div class="member-avatar-wrap">
                  <div class="member-avatar" :class="getMemberAvatarClass(index)">
                    {{ getMemberInitial(member) }}
                  </div>
                </div>
                <div class="member-name" :title="member">{{ member }}</div>
              </div>
            </div>
          </div>

          <!-- 底部提示 -->
        </div>

        <div v-else class="empty-state member-empty">
          <div class="empty-icon">
            <i class="el-icon-user-solid"></i>
          </div>
          <p>暂无成员数据</p>
        </div>

        <!-- 抽屉底部操作按钮 -->
        <div class="member-drawer-footer">
          <el-button round @click="memberDialogVisible = false">关闭</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
export default {
  name: 'ChatWindow',
  props: {
    // 是否显示聊天窗口
    visible: {
      type: Boolean,
      default: false
    },
    // 聊天窗口位置
    position: {
      type: Object,
      default: () => ({ x: 100, y: 100 })
    },
    // 当前用户信息
    currentUser: {
      type: Object,
      default: () => ({
        name: '',
        avatar: ''
      })
    },
    // 聊天群组列表
    chatGroupList: {
      type: Array,
      default: () => []
    },
    // 聊天消息列表
    chatMessages: {
      type: Array,
      default: () => []
    },
    // 默认选中的群组
    defaultGroup: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      // 聊天相关数据
      inputMessage: '', // 输入的消息内容
      canSend: false, // 是否可以发送消息

      // 拖拽相关数据
      isDragging: false, // 是否正在拖拽
      dragOffset: { x: 0, y: 0 }, // 拖拽偏移量

      // 选中的群组
      selectedGroup: {},
      memberDialogVisible: false,

      // 新增:搜索关键字、窗口尺寸(用于边界限制)
      searchKeyword: '',
      windowSize: { width: 880, height: 620 }
    }
  },
  computed: {
    // 实时计算窗口位置(跟随 prop)
    windowStyle() {
      return {
        left: this.position.x + 'px',
        top: this.position.y + 'px',
        width: this.windowSize.width + 'px',
        height: this.windowSize.height + 'px'
      }
    },
    // 侧栏群组过滤
    filteredGroupList() {
      if (!this.searchKeyword.trim()) return this.chatGroupList
      const kw = this.searchKeyword.trim().toLowerCase()
      return this.chatGroupList.filter(
        g => g.name && g.name.toLowerCase().includes(kw)
      )
    },
    // 头部状态文案
    statusText() {
      if (!this.selectedGroup.id) return '选择群聊开始聊天'
      const count = (this.selectedGroup.memberList || []).length
      return  '在线'
    },
    // 按时间分组的聊天消息(用于显示时间分隔,扁平化为单层数组,便于 v-for key)
    groupedMessages() {
      const groups = []
      let lastTime = ''
      this.chatMessages.forEach(msg => {
        const time = msg.time || this.formatTime(msg.timestamp || (typeof msg.id === 'number' ? msg.id : Date.now()))
        if (time !== lastTime) {
          groups.push({ time, items: [msg] })
          lastTime = time
        } else {
          groups[groups.length - 1].items.push(msg)
        }
      })
      return groups
    },
    // 扁平化的渲染列表(时间分隔 + 消息)
    renderedMessages() {
      const list = []
      this.groupedMessages.forEach((group, gIdx) => {
        list.push({
          key: `t-${gIdx}`,
          kind: 'time',
          time: group.time
        })
        group.items.forEach(msg => {
          list.push({
            key: `m-${msg.id}`,
            kind: 'message',
            message: msg
          })
        })
      })
      return list
    }
  },
  methods: {
    openMemberDialog() {
      if (!this.selectedGroup.id) return
      this.memberDialogVisible = true
    },
    // 关闭聊天窗口
    handleClose() {
      this.$emit('close')
    },

    // 选择群组
    selectGroup(group) {
      this.selectedGroup = group
      this.$emit('group-selected', group)
    },

    // 输入时检查状态,控制发送按钮
    onInput() {
      this.checkInputStatus()
    },

    // 检查输入状态,控制发送按钮 + 字符上限
    checkInputStatus() {
      // 限制 500 字
      if (this.inputMessage.length > 500) {
        this.inputMessage = this.inputMessage.slice(0, 500)
      }
      this.canSend = this.inputMessage.trim().length > 0
    },

    // 发送消息
    sendMessage() {
      if (!this.canSend) return

      const message = this.inputMessage.trim()
      if (!message) return

      // 添加新消息到聊天列表(标记 isNew 用于发送后高亮)
      const newMessage = {
        id: Date.now(),
        type: 'own',
        content: message,
        avatar: this.currentUser.avatar,
        senderName: this.currentUser.name || '我',
        timestamp: Date.now(),
        _isNew: true
      }
      this.$emit('send-message', newMessage)
      // 清空输入框
      this.inputMessage = ''
      this.canSend = false
      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })
      // 移除高亮
      setTimeout(() => {
        newMessage._isNew = false
      }, 1500)
    },

    // 滚动到底部
    scrollToBottom() {
      const chatList = this.$refs.chatList
      if (chatList) {
        setTimeout(() => {
          chatList.scrollTop = chatList.scrollHeight
        }, 50)
      }
    },

    // 时间格式化(HH:mm)
    formatTime(ts) {
      if (!ts) return ''
      const t = typeof ts === 'number' ? ts : Date.parse(ts)
      if (isNaN(t)) return ''
      const d = new Date(t)
      const h = String(d.getHours()).padStart(2, '0')
      const m = String(d.getMinutes()).padStart(2, '0')
      return `${h}:${m}`
    },

    // 开始拖拽(仅响应 chat-header / chat-top)
    startDrag(event) {
      const target = event.target
      const dragArea = target.closest('.chat-header') ||
                       target.closest('.chat-top')
      if (!dragArea) return
      // 排除按钮、输入框、图标等可交互元素
      if (target.closest('button, input, textarea, i, .action-btn, .toolbar-icon, .sidebar-action, .top-extra')) return

      this.isDragging = true
      const rect = this.$el.getBoundingClientRect()
      this.dragOffset.x = event.clientX - rect.left
      this.dragOffset.y = event.clientY - rect.top

      // 添加全局事件监听
      document.addEventListener('mousemove', this.handleDrag)
      document.addEventListener('mouseup', this.stopDrag)

      // 防止默认行为
      event.preventDefault()
    },

    // 处理拖拽
    handleDrag(event) {
      if (!this.isDragging) return

      const newX = event.clientX - this.dragOffset.x
      const newY = event.clientY - this.dragOffset.y

      // 限制拖拽范围,防止窗口完全拖出屏幕
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      const w = this.windowSize.width

      const newPosition = {
        x: Math.max(-w + 80, Math.min(newX, windowWidth - 80)),
        y: Math.max(0, Math.min(newY, windowHeight - 60))
      }

      // 通过事件向父组件更新位置
      this.$emit('position-change', newPosition)
    },

    // 停止拖拽
    stopDrag() {
      this.isDragging = false

      // 移除全局事件监听
      document.removeEventListener('mousemove', this.handleDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    },

    // 获取头像文字
    getAvatarText(message) {
      // 优先取 senderName 首字
      if (message.senderName) return message.senderName.substring(0, 1)
      // 兼容 senderType:0 专家 / 1 投标人
      if (message.senderType !== undefined) {
        return message.senderType === 0 ? '专' : '投'
      }
      return message.type === 'own' ? '我' : '群'
    },

    // 获取头像样式类
    getAvatarClass(message) {
      if (message.senderType !== undefined) {
        return message.senderType === 0 ? 'avatar-expert' : 'avatar-bidder'
      }
      return message.type === 'own' ? 'avatar-own' : 'avatar-other'
    },

    // 获取成员头像首字(兼容字符串/对象)
    getMemberInitial(member) {
      if (!member) return '?'
      const text = typeof member === 'string' ? member : (member.name || member.nickName || '')
      return String(text).substring(0, 1) || '?'
    },

    // 获取成员头像样式类(根据角色/位置切换渐变色)
    getMemberAvatarClass(index) {
      if (index === 0) return 'role-owner'
      if (index < 3) return 'role-admin'
      return 'role-member'
    },

    // 获取成员角色 class(用于样式)
    getMemberRoleClass(index) {
      if (index === 0) return 'is-owner'
      if (index < 3) return 'is-admin'
      return 'is-member'
    }
  },
  watch: {
    // 监听消息变化,自动滚动到底部
    chatMessages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      },
      deep: true
    },
    // 根据父组件传入的默认群组初始化/更新选中群组
    defaultGroup: {
      immediate: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length) {
          this.selectedGroup = newVal
        }
      }
    }
  },
  beforeDestroy() {
    // 销毁监听
    document.removeEventListener('mousemove', this.handleDrag)
    document.removeEventListener('mouseup', this.stopDrag)
  }
}
</script>

<style scoped lang="less">
// 聊天窗口
.chat-window {
  position: fixed;
  display: flex;
  flex-direction: column;
  z-index: 2000;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow:
    0 20px 50px rgba(0, 0, 0, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  user-select: none; // 防止拖拽时选中文本
  cursor: default;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  border: 1px solid rgba(0, 0, 0, 0.04);
  animation: chatFadeIn 0.25s ease;

  @keyframes chatFadeIn {
    from { opacity: 0; transform: scale(0.96) translateY(8px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }

  // ============ 顶部标题栏 ============
  .chat-header {
    height: 56px;
    padding: 0 12px 0 18px;
    background: linear-gradient(135deg, #4f7df9 0%, #6c5ce7 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: move;
    flex-shrink: 0;

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;

      .header-icon {
        width: 32px;
        height: 32px;
        background: rgba(255, 255, 255, 0.18);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
      }

      .header-title {
        .title-main {
          font-size: 15px;
          font-weight: 600;
          line-height: 1.2;
          max-width: 240px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .title-sub {
          font-size: 12px;
          opacity: 0.85;
          margin-top: 2px;
          display: flex;
          align-items: center;
          gap: 5px;

          .status-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #2ecc71;
            box-shadow: 0 0 0 2px rgba(46, 204, 113, 0.3);
          }
        }
      }
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 4px;

      .action-btn {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        border: 0;
        background: transparent;
        color: #fff;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        transition: background-color 0.2s;

        &:hover {
          background: rgba(255, 255, 255, 0.18);
        }
        &.close:hover {
          background: #ff4d4f;
        }
      }
    }
  }

  // ============ 主体 ============
  .chat-body {
    flex: 1;
    display: flex;
    min-height: 0;
  }

  // ============ 左侧栏 ============
  .chat-sidebar {
    width: 280px;
    border-right: 1px solid #eef0f4;
    background: #f8f9fb;
    display: flex;
    flex-direction: column;

    .sidebar-header {
      padding: 14px 16px 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .sidebar-title {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
      }

      .sidebar-action {
        width: 26px;
        height: 26px;
        border-radius: 6px;
        background: #fff;
        border: 1px solid #e4e7ed;
        cursor: pointer;
        color: #606266;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;

        &:hover {
          color: #4f7df9;
          border-color: #4f7df9;
        }
      }
    }

    .sidebar-search {
      margin: 4px 14px 10px;
      padding: 6px 10px;
      background: #fff;
      border-radius: 8px;
      border: 1px solid #eef0f4;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: border-color 0.2s;

      &:focus-within {
        border-color: #4f7df9;
      }

      i {
        color: #909399;
        font-size: 14px;
      }

      .search-input {
        flex: 1;
        border: none;
        outline: none;
        font-size: 13px;
        background: transparent;
        color: #303133;
      }
    }

    .group-list {
      flex: 1;
      overflow-y: auto;
      padding: 0 6px 8px;

      .group-item {
        display: flex;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        margin-bottom: 2px;
        position: relative;
        transition: background-color 0.18s;

        &:hover { background: rgba(79, 125, 249, 0.06); }

        &.active {
          background: linear-gradient(90deg, rgba(79, 125, 249, 0.12), rgba(108, 92, 231, 0.08));

          .group-name { color: #4f7df9; font-weight: 600; }
        }

        .group-avatar {
          position: relative;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          flex-shrink: 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-weight: 600;
          font-size: 14px;

          .avatar-text {
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
          }

          .unread-badge {
            position: absolute;
            top: -4px;
            right: -4px;
            min-width: 18px;
            height: 18px;
            padding: 0 4px;
            background: #ff4d4f;
            color: #fff;
            border-radius: 9px;
            border: 2px solid #f8f9fb;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: 600;
          }
        }

        .group-info {
          flex: 1;
          min-width: 0;

          .group-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2px;
          }

          .group-name {
            font-size: 14px;
            color: #303133;
            font-weight: 500;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 140px;
          }

          .group-time {
            font-size: 11px;
            color: #909399;
            flex-shrink: 0;
          }

          .group-preview {
            display: flex;
            justify-content: space-between;
            gap: 6px;
            align-items: center;
          }

          .preview-text {
            flex: 1;
            font-size: 12px;
            color: #909399;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .member-tag {
            font-size: 11px;
            color: #c0c4cc;
            flex-shrink: 0;
          }
        }
      }
    }

    .empty-state {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #c0c4cc;
      gap: 8px;
      padding: 40px 0;

      i { font-size: 32px; }
      p { margin: 0; font-size: 13px; }
    }
  }

  // ============ 右侧主区 ============
  .chat-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: #ffffff;

    .chat-top {
      height: 60px;
      padding: 0 18px;
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: move;
      border-bottom: 1px solid #eef0f4;
      background: #fff;
      flex-shrink: 0;

      .top-avatar {
        width: 38px;
        height: 38px;
        border-radius: 10px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-weight: 600;
        flex-shrink: 0;
        cursor: default;

        &.clickable {
          cursor: pointer;
          transition: transform 0.2s;
          &:hover { transform: scale(1.05); }
        }

        i { font-size: 20px; }
      }

      .top-info {
        flex: 1;
        min-width: 0;
        .top-name {
          font-size: 15px;
          font-weight: 600;
          color: #303133;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .top-meta {
          font-size: 12px;
          color: #909399;
          margin-top: 2px;
        }
      }

      .top-extra {
        display: flex;
        gap: 14px;

        i {
          font-size: 18px;
          color: #606266;
          cursor: pointer;
          transition: color 0.2s;
          &:hover { color: #4f7df9; }
        }
      }
    }

    // ============ 消息区 ============
    .chat-content {
      flex: 1;
      overflow-y: auto;
      padding: 18px 22px 8px;
      background: linear-gradient(180deg, #fcfcfd 0%, #f6f8fb 100%);

      .time-divider {
        display: flex;
        justify-content: center;
        margin: 12px 0 14px;

        span {
          font-size: 12px;
          color: #909399;
          background: rgba(144, 147, 153, 0.1);
          padding: 2px 10px;
          border-radius: 10px;
        }
      }

      .message-wrapper {
        display: flex;
        align-items: flex-start;
        margin-bottom: 16px;
        gap: 10px;
        animation: msgIn 0.25s ease;

        @keyframes msgIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .avatar {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);

          &.avatar-own { background: linear-gradient(135deg, #4f7df9 0%, #6c5ce7 100%); }
          &.avatar-other { background: linear-gradient(135deg, #909399 0%, #606266 100%); }
          &.avatar-expert { background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%); }
          &.avatar-bidder { background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%); }
        }

        .message-body {
          max-width: 65%;
          min-width: 40px;

          .sender-name {
            font-size: 12px;
            color: #909399;
            margin-bottom: 3px;
            padding-left: 2px;
          }

          .bubble {
            padding: 9px 13px;
            border-radius: 12px;
            font-size: 14px;
            line-height: 1.5;
            word-break: break-word;
            word-wrap: break-word;
            position: relative;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
            transition: box-shadow 0.2s;
            display: inline-block;
          }

          .bubble-other {
            background: #ffffff;
            color: #303133;
            border: 1px solid #eef0f4;
            border-top-left-radius: 4px;
          }

          .bubble-own {
            background: linear-gradient(135deg, #4f7df9 0%, #6c5ce7 100%);
            color: #ffffff;
            border-top-right-radius: 4px;
            box-shadow: 0 2px 8px rgba(79, 125, 249, 0.25);
          }

          .bubble.is-new {
            animation: bubblePulse 1.4s ease;
          }
          @keyframes bubblePulse {
            0% { box-shadow: 0 0 0 0 rgba(79, 125, 249, 0.4); }
            70% { box-shadow: 0 0 0 8px rgba(79, 125, 249, 0); }
            100% { box-shadow: 0 0 0 0 rgba(79, 125, 249, 0); }
          }
        }

        // 自己消息 - 右侧对齐
        &.own {
          flex-direction: row-reverse;

          .message-body {
            text-align: right;

            .bubble {
              display: inline-block;
            }
          }
        }
      }

      .empty-messages {
        flex: 1;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #c0c4cc;
        gap: 12px;
        min-height: 260px;

        .empty-icon {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f0f4ff 0%, #f5f0ff 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          color: #4f7df9;
        }

        p { margin: 0; font-size: 14px; color: #909399; }
      }
    }

    // ============ 底部输入区 ============
    .chat-footer {
      border-top: 1px solid #eef0f4;
      background: #fff;
      padding: 8px 16px 12px;
      flex-shrink: 0;

      .toolbar {
        display: flex;
        gap: 6px;
        padding: 4px 0 8px;
        border-bottom: 1px solid #f4f4f7;
        margin-bottom: 8px;

        .toolbar-icon {
          font-size: 18px;
          color: #909399;
          padding: 4px 8px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;

          &:hover {
            color: #4f7df9;
            background: rgba(79, 125, 249, 0.08);
          }
        }
      }

      .input-wrapper {
        border: 1px solid #e4e7ed;
        border-radius: 8px;
        padding: 8px 10px;
        background: #fafbfc;
        transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;

        &:focus-within {
          border-color: #4f7df9;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(79, 125, 249, 0.08);
        }

        .message-input {
          width: 100%;
          border: none;
          outline: none;
          resize: none;
          background: transparent;
          font-size: 14px;
          line-height: 1.5;
          max-height: 120px;
          min-height: 36px;
          font-family: inherit;
          color: #303133;
        }
      }

      .footer-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: 8px;

        .char-count {
          font-size: 12px;
          color: #c0c4cc;
          transition: color 0.2s;
          &.overflow { color: #ff4d4f; }
        }

        .send-button {
          background: linear-gradient(135deg, #4f7df9 0%, #6c5ce7 100%);
          color: #fff;
          border: none;
          border-radius: 16px;
          padding: 6px 16px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s;
          box-shadow: 0 2px 6px rgba(79, 125, 249, 0.3);

          i { font-size: 13px; }

          &:hover:not(.disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(79, 125, 249, 0.4);
          }
          &:active:not(.disabled) {
            transform: translateY(0);
          }

          &.disabled {
            background: #c0c4cc !important;
            box-shadow: none;
            cursor: not-allowed;
            color: #fff;
          }
        }
      }
    }
  }
}
</style>

<style lang="less">
// 群成员抽屉(从聊天窗口右侧滑出,微信/钉钉电脑版风格)
.member-drawer {
  // 抽屉外层样式
  background: #ffffff;
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.08);
  border-radius: 12px 0 0 12px;
  overflow: hidden;
}

.member-drawer-inner {
  display: flex;
  flex-direction: column;
  height: 100%;

  // ============ 抽屉顶部 ============
  .member-drawer-header {
    padding: 22px 24px 18px;
    background: linear-gradient(135deg, #f8f9fc 0%, #f3f5fb 100%);
    border-bottom: 1px solid #f0f0f3;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;

    .header-content {
      display: flex;
      align-items: center;
      gap: 14px;
      flex: 1;
      min-width: 0;
    }

    .header-avatar {
      width: 56px;
      height: 56px;
      border-radius: 14px;
      background: linear-gradient(135deg, #4f7df9 0%, #6c5ce7 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(79, 125, 249, 0.25);
      flex-shrink: 0;

      .avatar-text {
        color: #fff;
        font-size: 20px;
        font-weight: 600;
      }
    }

    .header-info {
      flex: 1;
      min-width: 0;
      .header-name {
        font-size: 17px;
        font-weight: 600;
        color: #1f2329;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .header-meta {
        font-size: 13px;
        color: #86909c;
        margin-top: 4px;
        display: flex;
        align-items: center;
        gap: 12px;

        span {
          display: inline-flex;
          align-items: center;
        }
      }
    }

    .header-close {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      border: 0;
      background: transparent;
      color: #86909c;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      transition: all 0.2s;
      flex-shrink: 0;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
        color: #1f2329;
      }
    }
  }

  // ============ 抽屉主体 ============
  .member-drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 18px 24px 20px;
  }

  // 快捷操作栏
  .quick-actions {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    padding: 16px 12px;
    margin-bottom: 16px;
    background: #fafbfc;
    border-radius: 10px;

    .quick-action {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      padding: 6px 0;
      border-radius: 8px;
      transition: all 0.2s;

      &:hover {
        background: rgba(79, 125, 249, 0.06);

        .action-icon {
          transform: translateY(-2px);
        }
      }

      span {
        font-size: 12px;
        color: #4e5969;
      }

      .action-icon {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 18px;
        transition: transform 0.2s;

        &.invite { background: linear-gradient(135deg, #4f7df9, #6c5ce7); }
        &.announce { background: linear-gradient(135deg, #ff9a3c, #ff6b35); }
        &.file { background: linear-gradient(135deg, #2ecc71, #27ae60); }
        &.manage { background: linear-gradient(135deg, #909399, #606266); }
      }
    }
  }

  // ============ 成员列表 ============
  .member-section {
    .section-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: #86909c;
      font-weight: 500;
      margin-bottom: 14px;
      padding: 0 4px;

      .section-count {
        background: #f0f3fa;
        color: #4f7df9;
        padding: 1px 7px;
        border-radius: 9px;
        font-size: 11px;
        font-weight: 600;
      }
    }

    .member-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 14px 8px;
      max-height: 320px;
      overflow-y: auto;
      padding: 4px;
    }

    .member-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      padding: 8px 4px 6px;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #f5f7fa;
        transform: translateY(-2px);

        .member-avatar {
          box-shadow: 0 6px 16px rgba(79, 125, 249, 0.25);
        }
      }

      .member-avatar-wrap {
        position: relative;
        width: 52px;
        height: 52px;
      }

      .member-avatar {
        width: 52px;
        height: 52px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 20px;
        font-weight: 600;
        transition: box-shadow 0.2s;

        &.role-owner { background: linear-gradient(135deg, #ff6b35, #ff9a3c); }
        &.role-admin { background: linear-gradient(135deg, #4f7df9, #6c5ce7); }
        &.role-member { background: linear-gradient(135deg, #95a5a6, #7f8c8d); }
      }

      .role-badge {
        position: absolute;
        bottom: -2px;
        right: -2px;
        font-size: 10px;
        padding: 2px 5px;
        border-radius: 8px;
        color: #fff;
        font-weight: 500;
        border: 2px solid #fff;
        white-space: nowrap;

        &.owner { background: #ff6b35; }
        &.admin { background: #4f7df9; }
      }

      .member-name {
        font-size: 12px;
        color: #4e5969;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: center;
      }
    }
  }

  // 底部提示
  .member-footer-tip {
    margin-top: 16px;
    padding: 10px 12px;
    background: #f5f7fa;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #86909c;

    i { color: #4f7df9; }
  }

  // 空状态
  .member-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 0;

    .empty-icon {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: linear-gradient(135deg, #f0f4ff 0%, #f5f0ff 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 12px;

      i { font-size: 36px; color: #4f7df9; }
    }

    p { font-size: 13px; color: #86909c; }
  }

  // ============ 抽屉底部 ============
  .member-drawer-footer {
    padding: 14px 20px;
    border-top: 1px solid #f0f0f3;
    background: #fafbfc;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
  }
}
</style>
